/**
 * A node's neighbourhood in the map graph (specs/map-graph.md): what leads to it and
 * what follows from it, read from the same graph the scene and pivots read. Pure.
 *
 * Incoming: every live edge into the node (its primary edge included, from a card or
 * from its town's start), plus the blockers and avoiders standing on its primary edge.
 * Outgoing: every live edge out of it, plus — when the node is a blocker or avoider —
 * the node it stands on. Each entry names the other end, the edge word and whether the
 * other end is in another scenario (a town chip).
 */

const uuidOf = (id) => (typeof id === 'string' && id.startsWith('n:') ? id.slice(2) : null);

/**
 * @param {object} graph   the map graph (API_ENDPOINTS.MAP_GRAPH)
 * @param {string} nodeId  a submission uuid (as the live map knows it)
 * @returns {{ node, scenario, incoming: Entry[], outgoing: Entry[] } | null}
 *   Entry: { key, type, other: { id, uuid, text, kind, valence, source, scenario, scenarioName }, crossScenario, via }
 */
export function neighbourhood(graph, nodeId) {
  if (!graph || !nodeId) return null;
  const id = `n:${nodeId}`;
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const node = nodeById.get(id);
  if (!node) return null;
  const scenarioName = new Map(graph.scenarios.map((s) => [s.id, s.attrs.name || s.attrs.slug]));
  const live = (e) => e.state !== 'rejected';

  const describe = (otherId) => {
    if (otherId.startsWith('s:')) {
      return { id: otherId, uuid: null, text: scenarioName.get(otherId) || '', kind: 'start', valence: null, source: null, scenario: otherId, scenarioName: scenarioName.get(otherId) || '' };
    }
    const o = nodeById.get(otherId);
    if (!o) return null;
    return {
      id: o.id, uuid: uuidOf(o.id), text: o.attrs.text, kind: o.attrs.kind, valence: o.attrs.valence,
      source: o.claims?.[0]?.by?.type || null, scenario: o.attrs.scenario, scenarioName: scenarioName.get(o.attrs.scenario) || '',
    };
  };
  const entry = (key, type, otherId, via) => {
    const other = describe(otherId);
    if (!other) return null;
    return { key, type, other, via, crossScenario: Boolean(other.scenario && node.attrs.scenario && other.scenario !== node.attrs.scenario) };
  };

  const edges = graph.edges.filter(live);
  const incoming = edges.filter((e) => e.attrs.to === id).map((e) => entry(e.id, e.attrs.type, e.attrs.from, e.attrs.primary ? 'primary' : 'link'));
  const outgoing = edges.filter((e) => e.attrs.from === id).map((e) => entry(e.id, e.attrs.type, e.attrs.to, e.attrs.primary ? 'primary' : 'link'));

  // Blockers / avoiders standing on the edge into this node act on it
  const intoThis = new Set(edges.filter((e) => e.attrs.to === id).map((e) => e.id));
  for (const b of graph.nodes.filter((n) => live(n) && ['blocker', 'avoider'].includes(n.attrs.kind) && intoThis.has(n.attrs.on))) {
    incoming.push(entry(`on:${b.id}`, b.attrs.kind === 'avoider' ? 'avoids' : 'blocks', b.id, 'blocker'));
  }
  // A blocker / avoider acts on the node it stands on
  if (['blocker', 'avoider'].includes(node.attrs.kind) && node.attrs.on) {
    const on = graph.edges.find((e) => e.id === node.attrs.on);
    if (on) outgoing.push(entry(`on:${id}`, node.attrs.kind === 'avoider' ? 'avoids' : 'blocks', on.attrs.to, 'blocker'));
  }

  const byScenarioThenText = (a, b) => Number(a.crossScenario) - Number(b.crossScenario) || a.other.text.localeCompare(b.other.text, 'fi');
  return {
    node,
    scenario: { id: node.attrs.scenario, name: scenarioName.get(node.attrs.scenario) || '' },
    incoming: incoming.filter(Boolean).sort(byScenarioThenText),
    outgoing: outgoing.filter(Boolean).sort(byScenarioThenText),
  };
}

/** The card's source in words: which kind, and the first evidence's document. */
export function sourceOf(node) {
  const claim = node?.claims?.[0];
  if (!claim) return null;
  const ev = claim.evidence?.[0];
  return { type: claim.by?.type || null, document: ev?.source || null, date: ev?.date || null, quote: ev?.quote || null };
}
